import { useEffect, useState } from 'preact/hooks';
import { fetchSessionByCode } from '../api';
import { languageLabel } from '../config';
import type { SessionMetadata } from '../types';

interface Props {
  shortCode: string;
  onPickLanguage: (lang: string) => void;
}

export function Landing({ shortCode, onPickLanguage }: Props) {
  const [state, setState] = useState<{ loading: boolean; session?: SessionMetadata; error?: string; code?: string }>({ loading: true });

  useEffect(() => {
    let alive = true;
    setState({ loading: true });
    fetchSessionByCode(shortCode)
      .then((session) => { if (alive) setState({ loading: false, session }); })
      .catch((err) => { if (alive) setState({ loading: false, error: err.message ?? 'Lookup failed', code: err.code }); });
    return () => { alive = false; };
  }, [shortCode]);

  if (state.loading) {
    return <CenterMessage title="Loading session…" subtitle={`Code: ${shortCode}`} />;
  }
  if (state.error || !state.session) {
    const sub = state.code === 'SESSION_NOT_FOUND'
      ? 'The session may have ended or the code is wrong.'
      : (state.error ?? 'Unable to load session.');
    return <CenterMessage title="Can't join this session" subtitle={sub} accent="danger" />;
  }

  const s = state.session;

  return (
    <main style={mainStyle}>
      <header>
        <div style={{ fontSize: 12, letterSpacing: 2, color: 'var(--text-muted)' }}>AIVOX LIVE</div>
        <h1 style={{ margin: '8px 0 4px', fontSize: 26, fontWeight: 700 }}>{s.topic ?? 'Live Broadcast'}</h1>
        {s.speaker_name ? (
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 16 }}>by {s.speaker_name}</p>
        ) : null}
        <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Pill>Source · {languageLabel(s.source_language)}</Pill>
          <Pill>{s.current_listeners}/{s.capacity} listening</Pill>
          <Pill>{s.tier === 'max' ? 'Max' : 'Pro'} tier</Pill>
        </div>
      </header>

      <section style={{ marginTop: 32 }}>
        <h2 style={{ fontSize: 13, letterSpacing: 1.5, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 12 }}>
          Choose your language
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {s.listener_languages.map((lang) => (
            <button key={lang} onClick={() => onPickLanguage(lang)} style={langBtn}>
              <span style={{ fontSize: 17, fontWeight: 600 }}>{languageLabel(lang)}</span>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{lang}</span>
            </button>
          ))}
        </div>
      </section>

      <footer style={{ marginTop: 'auto', paddingTop: 32, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
        Code: {s.short_code} · Expires {new Date(s.expires_at).toLocaleTimeString()}
      </footer>
    </main>
  );
}

function Pill({ children }: { children: any }) {
  return (
    <span style={{ padding: '5px 12px', borderRadius: 999, background: 'var(--surface)', border: '1px solid var(--border)', fontSize: 12, color: 'var(--text-muted)' }}>
      {children}
    </span>
  );
}

function CenterMessage({ title, subtitle, accent }: { title: string; subtitle: string; accent?: 'danger' }) {
  return (
    <main style={{ ...mainStyle, justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: accent === 'danger' ? 'var(--danger)' : 'var(--text)' }}>{title}</div>
      <div style={{ marginTop: 8, color: 'var(--text-muted)', fontSize: 15 }}>{subtitle}</div>
    </main>
  );
}

const mainStyle: any = { padding: '36px 24px', maxWidth: 520, margin: '0 auto', minHeight: '100dvh', display: 'flex', flexDirection: 'column' };
const langBtn: any = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '18px 20px', borderRadius: 12,
  background: 'var(--surface)', color: 'var(--text)',
  border: '1px solid var(--border)', cursor: 'pointer', textAlign: 'left',
};
